<template>
  <div class="container-fluid py-4">

```
<!-- Notification -->
<div v-if="notification.show" :class="['notification', notification.type]">
  {{ notification.message }}
</div>

<!-- Header -->
<div class="d-flex justify-content-between align-items-center mb-3">
  <h2>User Management</h2>

  <div class="d-flex align-items-center gap-2">
    <input
      type="text"
      v-model="searchQuery"
      placeholder="Search by name, email, role..."
      class="form-control form-control-sm mr-2"
      style="width:250px"
    />
    <button class="btn btn-primary" @click="openAddUserModal">
      + Add New User
    </button>
  </div>
</div>

<!-- Users Table -->
<div class="card shadow-sm">
  <div class="card-body p-0">
    <table class="table mb-0">
      <thead class="thead-dark">
        <tr>
          <th>Name</th>
          <th>Email</th>
          <th>Role</th>
          <th width="300">Actions</th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="user in paginatedUsers" :key="user.id">
          <td>{{ user.name }}</td>
          <td>{{ user.email }}</td>
          <td>{{ user.role }}</td>

          <td>
            <div class="action-buttons">

              <!-- View -->
              <button
                class="btn btn-sm btn-info"
                @click="viewUser(user)"
              >
                View
              </button>

              <!-- Role -->
              <select
                class="form-control form-control-sm"
                v-model="user.role"
                @change="changeRole(user)"
              >
                <option value="admin">Admin</option>
                <option value="coordinator">Coordinator</option>
                <option value="member">Member</option>
              </select>

              <!-- Status -->
              <button
                class="btn btn-sm"
                :class="user.status === 'active' ? 'btn-danger' : 'btn-success'"
                @click="toggleStatus(user)"
              >
                {{ user.status === 'active' ? 'Suspend' : 'Activate' }}
              </button>

            </div>
          </td>
        </tr>

        <tr v-if="paginatedUsers.length === 0">
          <td colspan="4" class="text-center py-3">
            No users found
          </td>
        </tr>

      </tbody>
    </table>
  </div>
</div>

<!-- Pagination -->
<div class="d-flex justify-content-center mt-3">
  <nav>
    <ul class="pagination">

      <li class="page-item" :class="{disabled:currentPage===1}">
        <button class="page-link" @click="prevPage">Previous</button>
      </li>

      <li
        v-for="page in totalPages"
        :key="page"
        class="page-item"
        :class="{active:currentPage===page}"
      >
        <button class="page-link" @click="currentPage = page">
          {{ page }}
        </button>
      </li>

      <li class="page-item" :class="{disabled:currentPage===totalPages}">
        <button class="page-link" @click="nextPage">Next</button>
      </li>

    </ul>
  </nav>
</div>

<!-- Add User Modal -->
<div v-if="showAddModal" class="custom-modal">
  <div class="modal-box dark-modal">

    <div class="modal-header">
      <h4>Add New User</h4>
      <button class="close-btn" @click="showAddModal=false">&times;</button>
    </div>

    <form @submit.prevent="submitAddUser">

      <label>Name</label>
      <input type="text" v-model="newUser.name" required />

      <label>Email</label>
      <input type="email" v-model="newUser.email" required />

      <label>Password</label>
      <input type="password" v-model="newUser.password" required />

      <label>Confirm Password</label>
      <input type="password" v-model="newUser.confirmPassword" required />

      <label>Phone</label>
      <input type="text" v-model="newUser.phone" />

      <label>Role</label>
      <select v-model="newUser.role">
        <option value="admin">Admin</option>
        <option value="coordinator">Coordinator</option>
        <option value="member">Member</option>
      </select>

      <label>Status</label>
      <select v-model="newUser.status">
        <option value="active">Active</option>
        <option value="inactive">Inactive</option>
      </select>

      <div class="text-right mt-3">
        <button
          type="button"
          class="btn btn-secondary mr-2"
          @click="showAddModal=false"
        >
          Cancel
        </button>

        <button type="submit" class="btn btn-primary">
          Save
        </button>
      </div>

    </form>
  </div>
</div>

<!-- View Modal -->
<div v-if="showModal" class="custom-modal">

  <div class="modal-box dark-modal">

    <div class="modal-header">
      <h4>User Details</h4>
      <button class="close-btn" @click="showModal=false">&times;</button>
    </div>

    <div class="modal-body">

      <p><strong>Name:</strong> {{selectedUser.name}}</p>
      <p><strong>Email:</strong> {{selectedUser.email}}</p>
      <p><strong>Phone:</strong> {{selectedUser.phone || 'N/A'}}</p>
      <p><strong>Role:</strong> {{selectedUser.role}}</p>
      <p><strong>Status:</strong> {{selectedUser.status || 'N/A'}}</p>

      <p v-if="selectedUser.created_at">
        <strong>Created:</strong> {{formatDate(selectedUser.created_at)}}
      </p>

      <p v-if="selectedUser.updated_at">
        <strong>Updated:</strong> {{formatDate(selectedUser.updated_at)}}
      </p>

    </div>

    <div class="text-right mt-3">
      <button class="btn btn-secondary" @click="showModal=false">
        Close
      </button>
    </div>

  </div>
</div>
```

  </div>
</template>

<script>

import axios from "axios"

export default {

name:"UserManagement",

data(){
return{

users:[],
searchQuery:"",
currentPage:1,
perPage:5,

showAddModal:false,
showModal:false,

selectedUser:{},

newUser:{
name:"",
email:"",
password:"",
confirmPassword:"",
phone:"",
role:"member",
status:"active"
},

notification:{
show:false,
message:"",
type:"success"
}

}
},

computed:{

filteredUsers(){

if(!this.searchQuery) return this.users

const q=this.searchQuery.toLowerCase()

return this.users.filter(user=>

(user.name||"").toLowerCase().includes(q) ||
(user.email||"").toLowerCase().includes(q) ||
(user.role||"").toLowerCase().includes(q)

)

},

totalPages(){
return Math.ceil(this.filteredUsers.length/this.perPage) || 1
},

paginatedUsers(){

const start=(this.currentPage-1)*this.perPage

return this.filteredUsers.slice(start,start+this.perPage)

}

},

watch:{
searchQuery(){
this.currentPage=1
}
},

methods:{

fetchUsers(){

axios.get(
"https://geotalent-backend-production.up.railway.app/api/Users",
{
headers:{
Authorization:"Bearer "+this.$store.state.userData.token
}
}
)

.then(res=>{

const allUsers=res.data.data || res.data.users || []

this.users=allUsers.filter(u=>
["admin","member","coordinator"]
.includes((u.role||"").toLowerCase())
)

this.currentPage=1

})

.catch(err=>console.log(err))

},

viewUser(user){
this.selectedUser=user
this.showModal=true
},

changeRole(user){

axios.post(
"https://geotalent-backend-production.up.railway.app/api/Users/SetRole",
{
id:user.id,
role:(user.role||"").toLowerCase()
},
{
headers:{
Authorization:"Bearer "+this.$store.state.userData.token
}
}
)

.then(res=>{

if(res.data.status==="success")
this.showNotification(res.data.data,"success")
else
this.showNotification("Role update failed","error")

})

},

toggleStatus(user){

const newStatus=user.status==="active"
? "suspended"
: "active"

const url=`https://geotalent-backend-production.up.railway.app/api/Users/SetStatus?id=${user.id}&status=${newStatus}`

axios.get(url,{
headers:{
Authorization:"Bearer "+this.$store.state.userData.token
}
})

.then(res=>{

if(res.data.status==="success"){
user.status=newStatus
this.showNotification(res.data.data,"success")
}else{
this.showNotification("Status update failed","error")
}

})

},

submitAddUser(){

axios.post(
"https://geotalent-backend-production.up.railway.app/api/Users/AddMember",
this.newUser,
{
headers:{
Authorization:"Bearer "+this.$store.state.userData.token
}
}
)

.then(res=>{

if(res.data.status==="success"){

this.showNotification(
res.data.data || "Member added successfully",
"success"
)

this.showAddModal=false
this.fetchUsers()

}

})

},

openAddUserModal(){

this.newUser={
name:"",
email:"",
password:"",
confirmPassword:"",
phone:"",
role:"member",
status:"active"
}

this.showAddModal=true

},

showNotification(message,type="success"){

this.notification.message=message
this.notification.type=type
this.notification.show=true

setTimeout(()=>{
this.notification.show=false
},3000)

},

formatDate(dateStr){
return dateStr ? new Date(dateStr).toLocaleString() : ""
},

nextPage(){
if(this.currentPage < this.totalPages)
this.currentPage++
},

prevPage(){
if(this.currentPage > 1)
this.currentPage--
}

},

mounted(){
this.fetchUsers()
}

}

</script>

<style scoped>

.table th{
background:#1f2937;
color:#fff;
}

/* FIX LIGHT MODE TEXT */
.table td{
color:inherit;
}

/* ACTION BUTTON ALIGN FIX */

.action-buttons{
  display:flex;
  align-items:center;
  gap:10px;
}

/* Same height for everything */
.action-buttons .btn{
  height:34px;
  display:flex;
  align-items:center;
  justify-content:center;
  padding:0 14px;
}

.action-buttons select{
  height:34px;
  min-width:140px;
  padding:0 10px;
}

.action-buttons select{
  margin-top: 8px;
}

/* Notification */

.notification{
position:fixed;
top:20px;
right:20px;
padding:12px 20px;
border-radius:6px;
font-weight:bold;
z-index:9999;
color:#fff;
}

.notification.success{
background:#22c55e;
}

.notification.error{
background:#ef4444;
}

/* Modal */

.custom-modal{
position:fixed;
inset:0;
background:rgba(0,0,0,0.6);
display:flex;
justify-content:center;
align-items:center;
z-index:999;
}

.dark-modal{
background:#1f2937;
color:#fff;
padding:25px;
width:450px;
border-radius:10px;
}

.modal-header{
display:flex;
justify-content:space-between;
align-items:center;
border-bottom:1px solid #374151;
padding-bottom:10px;
margin-bottom:15px;
}

.close-btn{
background:transparent;
border:none;
font-size:22px;
cursor:pointer;
color:#fff;
}

label{
font-weight:700;
margin-bottom:4px;
display:block;
}

input,select{
width:100%;
padding:8px 12px;
border-radius:6px;
margin-bottom:12px;
border:1px solid #4b5563;
background:#374151;
color:#fff;
}

</style>
